import { useCallback, useEffect, useState } from 'react';
import axios from '../config/api';
import type { OperationOrder } from '../components/OrderOperationsPanel';
import { sortNewestFirst } from '../utils/newestFirst';

const REPORT_PAGE_SIZE = 200;
const REPORT_MAX_ORDERS = 5000;

function unwrapOrders(payload: unknown): OperationOrder[] {
  if (Array.isArray(payload)) return payload as OperationOrder[];
  if (payload && typeof payload === 'object' && Array.isArray((payload as { orders?: unknown }).orders)) {
    return (payload as { orders: OperationOrder[] }).orders;
  }
  return [];
}

function hasMorePages(payload: unknown, fetchedCount: number): boolean {
  if (payload && typeof payload === 'object' && (payload as { pagination?: { has_more?: boolean } }).pagination) {
    return Boolean((payload as { pagination: { has_more?: boolean } }).pagination.has_more);
  }
  return fetchedCount >= REPORT_PAGE_SIZE;
}

export function useOrdersReport(selectedCafe: string, start: Date, end: Date) {
  const [orders, setOrders] = useState<OperationOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const collected: OperationOrder[] = [];
      let offset = 0;
      let hitCap = false;
      while (true) {
        const params: Record<string, string> = {
          limit: String(REPORT_PAGE_SIZE),
          offset: String(offset),
          include_meta: '1',
          created_from: start.toISOString(),
          created_to: end.toISOString(),
        };
        if (selectedCafe) params.cafe_id = selectedCafe;
        const { data } = await axios.get<
          OperationOrder[] | { orders?: OperationOrder[]; pagination?: { has_more?: boolean } }
        >('/api/orders/admin/all', { params });
        const batch = unwrapOrders(data);
        collected.push(...batch);
        if (collected.length >= REPORT_MAX_ORDERS) {
          hitCap = true;
          break;
        }
        if (!hasMorePages(data, batch.length) || batch.length === 0) break;
        offset += batch.length;
      }
      setOrders(sortNewestFirst(collected.slice(0, REPORT_MAX_ORDERS), (order) => order.created_at));
      setTruncated(hitCap);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to load orders for this period';
      setError(msg);
      setOrders([]);
      setTruncated(false);
    } finally {
      setLoading(false);
    }
  }, [selectedCafe, start.toISOString(), end.toISOString()]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    orders,
    loading,
    error,
    truncated,
    reload: load,
    count: orders.length,
  };
}
